<template>
  <div class="flex h-screen bg-slate-950 font-outfit overflow-hidden text-slate-300">
    <!-- Sidebar / Filter Bar -->
    <PlansSidebar 
        v-model:searchQuery="searchQuery"
        v-model:selectedDeveloper="selectedDeveloper"
        :developers="developers"
        :projects-count="projects.length"
        :total-plans-count="totalPlansCount"
        :is-admin="isAdmin"
        @reset-filters="resetFilters"
        @logout="logout"
        @open-admin="router.push({ name: 'plans_admin' })"
    />

    <!-- Main Content -->
    <main class="flex-1 flex flex-col min-w-0 relative bg-slate-950">
      <header class="h-24 border-b border-slate-800 px-10 flex items-center justify-between z-10 bg-slate-950/80 backdrop-blur-md">
        <div class="flex flex-col">
           <h1 class="text-xl font-black text-white tracking-widest flex items-center gap-3 uppercase">
             <Layers class="w-6 h-6 text-blue-500" />
             Pipeline projets
           </h1>
           <p class="text-[9px] text-slate-600 font-bold uppercase tracking-[0.3em] mt-1 italic">Listing_Snapshot: {{ new Date().toLocaleDateString() }}</p>
        </div>
        
        <div class="flex items-center gap-6">
            <div class="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl">
               <div class="w-2 h-2 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.5)]"></div>
               <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Moteur_Discovery: ON</span>
            </div>
            <button 
              @click="refreshData" 
              :disabled="isRefreshing" 
              aria-label="Rafraîchir les données du projet"
              :aria-busy="isRefreshing"
              class="w-10 h-10 bg-slate-900 border border-slate-800 rounded-xl flex items-center justify-center hover:border-blue-500/50 transition-all group"
            >
               <RotateCw :class="cn('w-4 h-4 text-slate-500 group-hover:text-blue-400 transition-colors', isRefreshing && 'animate-spin')" aria-hidden="true" />
            </button>
        </div>
      </header>

      <div class="flex-1 overflow-y-auto p-10 relative">
        <!-- Blueprint Decorations -->
        <Compass class="absolute top-20 right-20 w-64 h-64 text-blue-500/5 pointer-events-none -rotate-12" />
        <Ruler class="absolute bottom-20 left-20 w-96 h-12 text-blue-500/5 pointer-events-none rotate-12" />

        <ProjectGrid 
            :projects="filteredProjects"
            @open="openProject"
            @reset-filters="resetFilters"
        />
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { 
  Compass, Layers, RotateCw, Ruler
} from 'lucide-vue-next'
import { cn } from '@/lib/utils'
import { useGlobalState } from '../../composables/useGlobalState'

// Sub-components
import PlansSidebar from './Dashboard/PlansSidebar.vue'
import ProjectGrid from './Dashboard/ProjectGrid.vue'

const router = useRouter()
const projects = ref([])
const developers = ref([])
const isLoading = ref(true)
const isRefreshing = ref(false)
const { isAdmin: isGlobalAdmin, getAuthHeader } = useGlobalState()
const isAdmin = ref(false)

const searchQuery = ref('')
const selectedDeveloper = ref(null)

const fetchInitialData = async () => {
    isLoading.value = true
    try {
        const [projectsRes, devsRes] = await Promise.all([
            fetch('/api/plans/projects', {
                headers: getAuthHeader()
            }),
            fetch('/api/plans/developers', {
                headers: getAuthHeader()
            })
        ])

        if (projectsRes.status === 401) {
            router.push({ name: 'plans_login' })
            return
        }

        projects.value = await projectsRes.json()
        developers.value = await devsRes.json()
    } catch (e) {
        console.error('Failed to fetch dashboard data', e)
    } finally {
        isLoading.value = false
        checkAdmin()
    }
}

const checkAdmin = async () => {
    try {
        const res = await fetch('/api/plans/admin/stats', {
            headers: getAuthHeader()
        })
        isAdmin.value = res.ok
    } catch (e) {
        isAdmin.value = false
    }
}

const refreshData = async () => {
    isRefreshing.value = true
    await fetchInitialData()
    isRefreshing.value = false
}

const filteredProjects = computed(() => {
    return projects.value.filter(project => {
        const matchesSearch = !searchQuery.value || project.name.toLowerCase().includes(searchQuery.value.toLowerCase())
        const matchesDeveloper = !selectedDeveloper.value || project.developer === selectedDeveloper.value
        return matchesSearch && matchesDeveloper
    })
})

const totalPlansCount = computed(() => {
    return projects.value.reduce((acc, p) => acc + parseInt(p.apartmentCount || 0), 0)
})

const resetFilters = () => {
    searchQuery.value = ''
    selectedDeveloper.value = null
}

const logout = () => {
    localStorage.removeItem('apiToken')
    router.push({ name: 'plans_login' })
}

const openProject = (project) => {
    router.push({ name: 'plans_project_detail', params: { id: project.id } })
}

onMounted(() => {
    fetchInitialData()
})
</script>

<style>
.blueprint-grid {
  background-image: 
    linear-gradient(rgba(59, 130, 246, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.05) 1px, transparent 1px);
  background-size: 40px 40px;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #1e293b;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: #334155;
}
</style>
